import { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatDate } from "../utils";

const ENTITY_LABELS = {
    "": "Toutes les entités",
    invoice: "Factures",
    payment: "Paiements",
    credit_note: "Notes de crédit",
    expense: "Dépenses",
    quote: "Soumissions",
    price_settings: "Paramètres de prix",
    tax_settings: "Paramètres de taxes",
    migration: "Migrations",
};

const ACTION_BADGE = {
    create: "bg-[#e6f4ea] text-[#0b6b2f] border-[#8ec79d]",
    update: "bg-[#eaf1fb] text-[#2f4f7f] border-[#97b0d0]",
    delete: "bg-[#fbe8e8] text-[#8a1f1f] border-[#e8a5a5]",
    cancel: "bg-[#fdecd8] text-[#9a3b00] border-[#e8b588]",
    apply: "bg-[#e7f0fb] text-[#1d4ed8] border-[#9db9e8]",
    migrate: "bg-[#e7f0fb] text-[#1d4ed8] border-[#9db9e8]",
};

const ACTION_LABELS = {
    create: "Création", update: "Modification", delete: "Suppression",
    cancel: "Annulation", apply: "Application", migrate: "Migration",
};

const fmtTime = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return `${formatDate(iso)} ${d.toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}`;
};

export const AuditPanel = () => {
    const { http } = useAuth();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [entity, setEntity] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError("");
        try {
            const params = entity ? `?entity=${entity}&limit=200` : "?limit=200";
            const { data } = await http.get(`/admin/audit${params}`);
            setRows(data.entries || []);
            setTotal(data.total || 0);
        } catch (e) {
            setError(e?.response?.data?.detail || "Erreur de chargement du journal d'audit");
        } finally {
            setLoading(false);
        }
    }, [http, entity]);

    useEffect(() => { load(); }, [load]);

    const entityOptions = useMemo(() => Object.entries(ENTITY_LABELS), []);

    return (
        <div data-testid="audit-panel">
            <div className="flex items-start md:items-center justify-between mb-6 gap-3 flex-col md:flex-row">
                <div className="hidden md:block">
                    <div className="tech-stamp mb-1">Comptabilité · Traçabilité</div>
                    <h1 className="font-display font-bold uppercase text-3xl tracking-tight flex items-center gap-2">
                        <ShieldCheck className="w-7 h-7 text-[#2f4f7f]" /> Journal d'audit
                    </h1>
                </div>
                <div className="flex gap-2 flex-wrap w-full md:w-auto items-center">
                    <select value={entity} onChange={(e) => setEntity(e.target.value)}
                        data-testid="audit-entity-filter"
                        className="px-3 py-2 border border-[#c5d4e7] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#2f4f7f]">
                        {entityOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <button onClick={load} data-testid="audit-refresh" className="btn-secondary !py-2 !px-3 !text-xs">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    </button>
                </div>
            </div>

            <p className="text-xs text-[#4b5d7a] mb-3">
                {total} entrée{total > 1 ? "s" : ""} — qui, quoi, quand, avec les valeurs avant/après.
            </p>

            {error && <div className="mb-4 p-4 border border-red-400 bg-red-50 text-red-800 text-sm">{error}</div>}

            <div className="border border-[#dde5f0] bg-white">
                <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#dde5f0] bg-[#f3f6fb] tech-stamp">
                    <div className="col-span-3">Date / heure</div>
                    <div className="col-span-2">Auteur</div>
                    <div className="col-span-2">Entité</div>
                    <div className="col-span-2">Action</div>
                    <div className="col-span-3">Détails</div>
                </div>

                {rows.length === 0 && !loading && (
                    <div className="p-12 text-center text-[#4b5d7a] text-sm">Aucune entrée d'audit.</div>
                )}

                <ul>
                    {rows.map((r) => {
                        const isOpen = expanded === r.id;
                        return (
                            <li key={r.id} className="border-b border-[#dde5f0] last:border-b-0" data-testid={`audit-row-${r.id}`}>
                                <button
                                    onClick={() => setExpanded(isOpen ? null : r.id)}
                                    className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 md:px-6 py-3 items-center hover:bg-[#f3f6fb] transition-colors"
                                >
                                    <div className="md:col-span-3 text-xs text-[#4b5d7a] font-mono">{fmtTime(r.at)}</div>
                                    <div className="md:col-span-2 text-sm truncate">{r.actor || "—"}</div>
                                    <div className="md:col-span-2 text-sm">{ENTITY_LABELS[r.entity] || r.entity}</div>
                                    <div className="md:col-span-2">
                                        <span className={`tech-stamp px-2 py-1 border text-[10px] ${ACTION_BADGE[r.action] || "border-[#dde5f0]"}`}>
                                            {ACTION_LABELS[r.action] || r.action}
                                        </span>
                                    </div>
                                    <div className="md:col-span-3 text-xs text-[#4b5d7a] flex items-center justify-between gap-2">
                                        <span className="truncate">{r.meta?.reason || r.entity_id?.slice(0, 10) || ""}</span>
                                        {isOpen ? <ChevronUp className="w-3.5 h-3.5 shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 shrink-0" />}
                                    </div>
                                </button>
                                {isOpen && (
                                    <div className="px-4 md:px-6 pb-4 grid grid-cols-1 md:grid-cols-3 gap-3 bg-[#f8fafd]">
                                        <JsonBlock title="Avant" value={r.before} />
                                        <JsonBlock title="Après" value={r.after} />
                                        <JsonBlock title="Métadonnées" value={r.meta} />
                                    </div>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
};

const JsonBlock = ({ title, value }) => (
    <div>
        <span className="tech-stamp block mb-1">{title}</span>
        <pre className="text-[11px] bg-white border border-[#dde5f0] p-2 overflow-x-auto whitespace-pre-wrap break-words">
            {value && Object.keys(value).length ? JSON.stringify(value, null, 2) : "—"}
        </pre>
    </div>
);
